import { useCallback, useRef, useEffect } from 'react';
import { supabase, SUPABASE_URL, supabaseEh } from '@/lib/supabase';
import { generateBase58Id } from '@/lib/id';
import { useGemBalance } from './useGemBalance';
import { useUserInfo } from './useUserInfo';
import { COST_PER_VIDEO } from '@/lib/constants';

const FREE_VIDEOS = 1;

export type VideoStatus = 'idle' | 'pending' | 'success' | 'failed';

export interface VideoRequestParams {
  imageUrl: string;
  customPrompt?: string;
  resourceId: string;
  mediaType: 'chat_video' | 'video';
  characterId?: string | null;
  chatId?: string | null;
  mrId?: string; // to reuse pending media request after Edge function timeout
  mrOptions?: Record<string, string | boolean | undefined>; // Additional options for eh-media-request
}

export interface VideoRequestResult {
  success: boolean;
  data?: {
    success?: boolean;
    filePath?: string;
    mrId?: string;
    retryAfter?: number;
    error?: string;
  };
  retrying?: boolean;
}

export function useVideo() {
  const { getRefreshedBalance, refreshBalance } = useGemBalance();
  const { isSubscriber, is18Confirmed, user } = useUserInfo();
  
  // Refs to ensure fresh values in closures
  const videoRetryCountRef = useRef<Record<string, number>>({});
  const videoRetryTimeoutRef = useRef<Record<string, ReturnType<typeof setTimeout> | null>>({});
  const videoAbortControllerRef = useRef<Record<string, AbortController | null>>({});
  const videoAbortTimeoutRef = useRef<Record<string, ReturnType<typeof setTimeout> | null>>({});

  // Cleanup function
  const cleanupVideoRequest = useCallback((requestId: string) => {
    if (videoRetryTimeoutRef.current[requestId]) {
      clearTimeout(videoRetryTimeoutRef.current[requestId]!);
      videoRetryTimeoutRef.current[requestId] = null;
    }
    if (videoAbortTimeoutRef.current[requestId]) {
      clearTimeout(videoAbortTimeoutRef.current[requestId]!);
      videoAbortTimeoutRef.current[requestId] = null;
    }
    if (videoAbortControllerRef.current[requestId]) {
      videoAbortControllerRef.current[requestId]?.abort('Cleanup');
      videoAbortControllerRef.current[requestId] = null;
    }
  }, []);

  // Cleanup all video requests on unmount
  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      Object.keys(videoRetryTimeoutRef.current).forEach(cleanupVideoRequest);
      // eslint-disable-next-line react-hooks/exhaustive-deps
      Object.keys(videoAbortTimeoutRef.current).forEach(cleanupVideoRequest);
      // eslint-disable-next-line react-hooks/exhaustive-deps
      Object.keys(videoAbortControllerRef.current).forEach(cleanupVideoRequest);
    };
  }, [cleanupVideoRequest]);

  const handleRequestVideo = useCallback(async ({
    imageUrl,
    customPrompt,
    resourceId,
    mediaType,
    characterId,
    chatId,
    mrId,
    mrOptions = {}
  }: VideoRequestParams): Promise<VideoRequestResult> => {
    const requestId = `${resourceId}_${Date.now()}`;

    if (!user?.id) {
      return { success: false, data: { error: 'Account required' } };
    }
    
    // Check if user is eligible (first one free, or subscriber with enough gems, or monthly free video)
    const oneMonthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
    const { data: videoRequests, error } = await supabaseEh
      .from('media_requests')
      .select('id, created_at')
      .eq('user_id', user.id)
      .eq('media_type', mediaType)
      .eq('status', 'success')
      .order('created_at', { ascending: false })
      .limit(FREE_VIDEOS + 1);

    if (error) {
      if (import.meta.env.DEV) console.error('[DEV] Error checking for previous videos:', error);
      // proceed and let backend handle it
    }

    const monthlyFreeVideo = !isSubscriber && videoRequests?.[0]?.created_at && videoRequests[0].created_at < oneMonthAgo;
    const hasFreeVideo = (videoRequests?.length || 0) < FREE_VIDEOS || monthlyFreeVideo;

    if (!hasFreeVideo && !isSubscriber) {
      const balance = await getRefreshedBalance();
      if (!balance || balance.total < COST_PER_VIDEO) {
        return { success: false, data: { error: 'upgrade_required' } };
      }
    }

    const mediaRequestId = mrId || generateBase58Id('mr');

    try {
      // Detect Edge function timeout limit reached to open a new connection
      const abortController = new AbortController();
      videoAbortControllerRef.current[requestId] = abortController;
      const abortTimeout = setTimeout(() => {
        abortController.abort('Edge function timeout');
      }, 1000 * 140); // Edge function Gateway timeout is 150s
      videoAbortTimeoutRef.current[requestId] = abortTimeout;

      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData?.session?.access_token) {
        throw new Error('No authentication token available');
      }

      const mediaRequestResponse = await fetch(`${SUPABASE_URL}/functions/v1/eh-media-request`, {
        signal: abortController.signal,
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionData.session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mrId: mediaRequestId,
          media_type: mediaType,
          characterId,
          chatId,
          resource_id: resourceId,
          safeMode: !is18Confirmed,
          videoSourceImageUrl: imageUrl,
          videoImgDescription: customPrompt,
          videoImgPrompt: customPrompt,
          style: 'realistic', // Default style, can be overridden via mrOptions
          ...mrOptions, // Allow additional options to be injected
        })
      });

      if (!mediaRequestResponse.ok && mediaRequestResponse.status === 402) {
        if (isSubscriber) {
          await refreshBalance();
          return { success: false, data: { error: 'low_gem_balance' } };
        }
        return { success: false, data: { error: 'insufficient_gems' } };
      } else if (!mediaRequestResponse.ok && (videoRetryCountRef.current[requestId] || 0) > 0) { // retry only once after error
        if (import.meta.env.DEV) console.error('[DEV] Error requesting video:', await mediaRequestResponse.text());
        return { success: false };
      }

      const mediaData = await mediaRequestResponse.json();
      
      if (!mediaRequestResponse.ok || (mediaData?.retryAfter && (videoRetryCountRef.current[requestId] || 0) < 10)) {
        videoRetryCountRef.current[requestId] = (videoRetryCountRef.current[requestId] || 0) + 1;
        // retryAfter is an epoch timestamp, compute delay in ms (min 10s)
        const delay = Math.max(mediaData?.retryAfter ? mediaData.retryAfter - Date.now() : 10000, 10000);
        const retryTimeout = setTimeout(async () => {
          await handleRequestVideo({
            imageUrl,
            customPrompt,
            resourceId,
            mediaType,
            characterId,
            chatId,
            mrOptions
          });
        }, Math.max(delay, 0));
        videoRetryTimeoutRef.current[requestId] = retryTimeout;
        return { success: false, retrying: true };
      }

      if (import.meta.env.DEV) console.log('[DEV] handleRequestVideo data:', mediaData);

      if (!mediaData?.success || !mediaData?.filePath) {
        return { success: false };
      }

      await refreshBalance();
      videoRetryCountRef.current[requestId] = 0; // Reset retry count on success
      return { success: true, data: mediaData };
    } catch (err) {
      if (import.meta.env.DEV) console.error('[DEV]', err)
      if ((err instanceof DOMException && err.name === 'AbortError')
        || typeof err === 'string' && err.toLowerCase().includes('timeout'))
      { // Edge function Gateway timeout
        // call again without incrementing retryCount since it's not a retry after real failure
        return handleRequestVideo({
          imageUrl,
          customPrompt,
          resourceId,
          mediaType,
          characterId,
          chatId,
          mrId: mediaRequestId,
          mrOptions
        });
      }

      return { success: false };
    } finally {
      cleanupVideoRequest(requestId);
    }
  }, [getRefreshedBalance, isSubscriber, is18Confirmed, refreshBalance, cleanupVideoRequest, user?.id]);

  return {
    handleRequestVideo,
    COST_PER_VIDEO,
    FREE_VIDEOS
  };
} 